import { useState } from "react";
import { ArrowRight, ChevronDown, History } from "lucide-react";
import { format, parseISO } from "date-fns";
import { get } from "@/lib/api";
import { useAsync } from "@/lib/queries";
import { Button } from "@/components/ui/button";

const ACTIONS = {
  run_created: "created the run",
  run_updated: "edited the run",
  run_accepted: "accepted the open job",
  run_status_auto: "Run status updated automatically",
  address_added: "added address",
  address_updated: "edited address",
  address_removed: "removed address",
  status_changed: "changed status of",
  signature_added: "added the officer signature",
};
const PAGE = 15;

const Change = ({ c }) => (
  <li className="flex flex-wrap items-center gap-1.5 text-sm text-slate-700">
    <span className="font-medium text-slate-900">{c.label}:</span>
    {c.old != null && <><span className="rounded bg-rose-50 px-1.5 text-rose-700 line-through decoration-rose-300">{c.old}</span><ArrowRight className="h-3 w-3 text-slate-400" /></>}
    <span className="rounded bg-emerald-50 px-1.5 text-emerald-800">{c.new ?? "—"}</span>
  </li>
);

const Entry = ({ e, i }) => (
  <li className="relative border-l-2 border-slate-200 pb-4 pl-4 last:pb-0" data-testid={`change-log-entry-${i}`}>
    <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-blue-600" />
    <p className="text-sm">
      <span className="font-data text-xs text-slate-500">{format(parseISO(e.at), "dd/MM/yyyy HH:mm")}</span>
      <span className="mx-1.5 text-slate-300">·</span>
      {e.action === "run_status_auto"
        ? <span className="text-slate-700">{ACTIONS[e.action]} <span className="text-slate-500">(after {e.user_name}&apos;s change)</span></span>
        : <><span className="font-semibold text-slate-900">{e.user_name}</span> <span className="text-slate-700">{ACTIONS[e.action] || e.action}</span></>}
      {e.address && <span className="font-medium text-slate-900"> {e.address}</span>}
    </p>
    {e.changes.length > 0 && <ul className="mt-1.5 space-y-1">{e.changes.map((c) => <Change key={c.field} c={c} />)}</ul>}
  </li>
);

const LogBody = ({ runId, refreshKey }) => {
  const { data, loading } = useAsync(() => get(`/runs/${runId}/activity`), [runId, refreshKey]);
  const [shown, setShown] = useState(PAGE);
  const items = data || [];
  return (
    <div className="border-t border-slate-100 px-5 pb-5" data-testid="change-log-body">
      {loading && !data && <p className="mt-3 text-sm text-slate-500">Loading…</p>}
      {data && !items.length && <p className="mt-3 text-sm text-slate-500" data-testid="change-log-empty">No changes recorded yet.</p>}
      {items.length > 0 && <ol className="mt-4">{items.slice(0, shown).map((e, i) => <Entry key={e.id} e={e} i={i} />)}</ol>}
      {items.length > shown && (
        <Button variant="ghost" size="sm" className="mt-3 text-blue-700" onClick={() => setShown(shown + PAGE)} data-testid="change-log-more">
          <ChevronDown className="mr-1 h-4 w-4" /> Show more ({items.length - shown})
        </Button>
      )}
    </div>
  );
};

export const ChangeLog = ({ runId, refreshKey }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="change-log">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="change-log-toggle"
        className="flex w-full items-center justify-between gap-2 rounded-xl p-5 text-left hover:bg-slate-50">
        <span className="font-display flex items-center gap-2 text-lg font-bold text-slate-900"><History className="h-5 w-5 text-blue-700" /> Change log</span>
        <span className="flex items-center gap-1 text-sm font-medium text-blue-700">
          {open ? "Hide" : "Show"}<ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && <LogBody runId={runId} refreshKey={refreshKey} />}
    </section>
  );
};
